import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// RCP-441: wishlist disabled — WishlistService removed from import
import { CartService, ProductService } from '@core';
import { ProductLegacy } from '@models';

import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { NgxPaginationModule } from 'ngx-pagination';
import Swiper from 'swiper';
import { ProductListCardComponent } from '../product-list-card/product-list-card.component';
import { SwiperConfigService } from './swiper-config.service';

export type TempProductType = ProductLegacy | any;

@Component({
  selector: 'lib-product-cards',
  templateUrl: './product-cards.component.html',
  styleUrls: ['./product-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    FormsModule,
    NgxPaginationModule,
    NgxSliderModule,
    MatCardModule,
    ProductListCardComponent,
    MatIconModule,
  ],
})
export class ProductCardsComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly productService = inject(ProductService);
  private readonly cartService = inject(CartService);

  products = input<TempProductType[]>([]);
  withControls = input<boolean>(true);

  readonly swiperContainerRef = viewChild.required<ElementRef<HTMLElement>>('swiperContainer');
  swiper: Swiper | null = null;

  swiperConfigService = inject(SwiperConfigService);
  // RCP-441: wishlist disabled
  // private wishlistService = inject(WishlistService);

  /** Mirrors `swiper.snapGrid.length` — the real number of scrollable pages. */
  private readonly snapCount = signal<number>(1);
  /** Mirrors `swiper.snapIndex` — the page Swiper is actually resting on. */
  private readonly snapIndex = signal<number>(0);

  readonly totalPages = computed(() => this.snapCount());
  readonly currentPage = computed(() => this.snapIndex() + 1);

  constructor() {
    afterNextRender(() => this.swiperInit(), { injector: this.injector });

    effect(() => {
      if (this.swiper && this.products()?.length) {
        this.swiper.setBreakpoint();
        this.swiper.update();
        this.swiper.slideTo(0, 0);
        this.syncFromSwiper(this.swiper);
      }
    });
  }

  private swiperInit(): void {
    const sync = (swiper: Swiper): void => this.syncFromSwiper(swiper);

    this.swiper = new Swiper(this.swiperContainerRef().nativeElement, {
      ...this.swiperConfigService.swiperConfig,
      on: {
        init: sync,
        afterInit: sync,
        breakpoint: sync,
        resize: sync,
        update: sync,
        slidesLengthChange: sync,
        snapGridLengthChange: sync,
        snapIndexChange: sync,
        slideChange: sync,
        fromEdge: sync,
        toEdge: sync,
      },
    });

    this.swiper.update();
    this.syncFromSwiper(this.swiper);
  }

  /**
   * Page state is read back from Swiper instead of being derived from
   * `slidesPerView` arithmetic: a resize re-lays out the slides without emitting
   * `slideChange`, so any locally computed page number goes stale and the arrows
   * end up asking Swiper to move to the slide it is already on.
   */
  syncFromSwiper(swiper: Swiper): void {
    if (!swiper || swiper.destroyed) {
      return;
    }

    this.snapCount.set(Math.max(1, swiper.snapGrid?.length ?? 1));
    this.snapIndex.set(swiper.snapIndex ?? 0);
  }

  ngOnInit(): void {
    this.products()?.forEach((product: TempProductType) => {
      product.Name = product.Name?.replace('<br />', '<br aria-hidden="true" />');
    });
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
  }

  // RCP-441: wishlist disabled
  // toggleWishList(product: TempProductType): void {
  //   this.wishlistService.toggleWishlist(product);
  // }

  onFocus(index: number): void {
    this.swiper?.slideTo(index, 500);
  }

  addToCart(product: TempProductType): void {
    this.cartService.addCartProduct(product).subscribe({
      error: (err) => {
        console.error('Failed to add product to cart', err);
      },
    });
  }

  onShowProduct(p: TempProductType): void {
    this.productService.goToProductDetails(p);
  }

  /**
   * Only suppress dragging the carousel by the card, never `disable()` it:
   * a disabled Swiper also rejects `slideTo`/`slideNext`, and a missed
   * `pointerleave` (the action button is swapped out under the cursor) would
   * leave the arrows permanently inert.
   */
  onHoverOnAction(value: boolean): void {
    if (this.swiper) {
      this.swiper.allowTouchMove = !value;
    }
  }

  nextSlide(): void {
    if (!this.swiper) {
      return;
    }

    this.swiper.allowTouchMove = true;

    if (this.swiper.isEnd) {
      this.swiper.slideTo(0, 500);
    } else {
      this.swiper.slideNext(500);
    }
  }

  prevSlide(): void {
    if (!this.swiper) {
      return;
    }

    this.swiper.allowTouchMove = true;

    if (this.swiper.isBeginning) {
      this.swiper.slideTo(Math.max(0, this.swiper.slides.length - 1), 500);
    } else {
      this.swiper.slidePrev(500);
    }
  }
}
